(function () {
  const defaultMoneyFormat = '${{amount}}';
  const cartRateCacheKey = 'nianave:currency-rate-cache';

  /**
   * Format Shopify price values as plain text for the theme's price elements.
   * @param {number|string} cents - Price in hundredths, as returned by Shopify.
   * @param {string} format - Optional Shopify money format override.
   * @returns {string} Formatted price with the configured currency text and spacing.
   */
  const formatMoney = function (cents, format) {
      const numericCents = Number(cents);
      const amount = Number.isFinite(numericCents) ? numericCents / 100 : 0;
      const formatString = format || window.moneyFormat || defaultMoneyFormat;

      const formattedValue = formatString.replace(/\{\{\s*(\w+)\s*\}\}/g, (placeholder, name) => {
          let precision = 2;
          let thousands = ',';
          let decimal = '.';

          switch (name) {
              case 'amount':
                  break;
              case 'amount_no_decimals':
                  precision = 0;
                  break;
              case 'amount_with_comma_separator':
                  thousands = '.';
                  decimal = ',';
                  break;
              case 'amount_no_decimals_with_comma_separator':
                  precision = 0;
                  thousands = '.';
                  break;
              case 'amount_with_apostrophe_separator':
                  thousands = "'";
                  break;
              case 'amount_no_decimals_with_space_separator':
                  precision = 0;
                  thousands = ' ';
                  break;
              case 'amount_with_space_separator':
                  thousands = ' ';
                  decimal = ',';
                  break;
              case 'amount_with_period_and_space_separator':
                  thousands = ' ';
                  break;
              default:
                  return placeholder;
          }

          const [whole, fraction] = amount.toFixed(precision).split('.');
          const groupedWhole = whole.replace(/\d(?=(\d{3})+(?!\d))/g, (digit) => digit + thousands);

          return precision === 0 ? groupedWhole : groupedWhole + decimal + fraction;
      });

      // Shopify HTML formats can include wrappers and encoded spaces.
      // Price callers use textContent or escape the result before rendering it.
      if (/[<&]/.test(formattedValue)) {
          const template = document.createElement('template');
          template.innerHTML = formattedValue;
          return template.content.textContent;
      }

      return formattedValue;
  };

  const readRateCache = function () {
      try {
          return JSON.parse(window.sessionStorage.getItem(cartRateCacheKey) || '{}');
      } catch (error) {
          return {};
      }
  };

  const writeRateCache = function (cache) {
      try {
          window.sessionStorage.setItem(cartRateCacheKey, JSON.stringify(cache));
      } catch (error) {
      }
  };

  const getBaseCurrency = function () {
      const cartThresholdElement = document.querySelector('[data-base-currency]');
      return cartThresholdElement?.dataset.baseCurrency || window.shopCurrency || Shopify?.currency?.shopCurrency || Shopify?.currency?.currentCurrency || Shopify?.currency?.active || '';
  };

  const getActiveCurrency = function () {
      return Shopify?.currency?.active || document.querySelector('.currency-code')?.textContent?.trim() || getBaseCurrency();
  };

  const getCachedRate = function (baseCurrency, activeCurrency) {
      const cache = readRateCache();
      return Number(cache[`${baseCurrency}_${activeCurrency}`]);
  };

  const setCachedRate = function (baseCurrency, activeCurrency, rate) {
      if (!Number.isFinite(rate) || rate <= 0) {
          return;
      }

      const cache = readRateCache();
      cache[`${baseCurrency}_${activeCurrency}`] = rate;
      writeRateCache(cache);
  };

  const getDirectCurrencyRate = function (baseCurrency, activeCurrency) {
      if (!baseCurrency || !activeCurrency || baseCurrency === activeCurrency) {
          return 1;
      }

      const candidateRateSources = [
          () => Number(Shopify?.currency?.rate),
          () => {
              const rates = Shopify?.currency?.rates || Shopify?.Currency?.rates || window.Currency?.rates;
              if (!rates) {
                  return null;
              }

              const activeRate = Number(rates[activeCurrency]);
              const baseRate = Number(rates[baseCurrency] || 1);

              if (!Number.isFinite(activeRate) || activeRate <= 0 || !Number.isFinite(baseRate) || baseRate <= 0) {
                  return null;
              }

              return activeRate / baseRate;
          }
      ];

      for (const getRate of candidateRateSources) {
          const rate = getRate();
          if (Number.isFinite(rate) && rate > 0) {
              setCachedRate(baseCurrency, activeCurrency, rate);
              return rate;
          }
      }

      const cachedRate = getCachedRate(baseCurrency, activeCurrency);
      if (Number.isFinite(cachedRate) && cachedRate > 0) {
          return cachedRate;
      }

      return null;
  };

  const inferCurrencyRateFromCart = async function ({
      activeCartTotal,
      activeCurrency = getActiveCurrency(),
      baseCurrency = getBaseCurrency()
  } = {}) {
      if (!baseCurrency || !activeCurrency || baseCurrency === activeCurrency) {
          return 1;
      }

      if (!Number.isFinite(activeCartTotal) || activeCartTotal <= 0) {
          return getCachedRate(baseCurrency, activeCurrency);
      }

      const cartUrl = `${window.Shopify.routes.root}cart.js?currency=${baseCurrency}`;

      try {
          const response = await window.fetch(cartUrl, {
              credentials: 'same-origin',
              method: 'GET',
          });
          const baseCart = await response.json();
          const baseCartTotal = Number(baseCart?.total_price);

          if (!Number.isFinite(baseCartTotal) || baseCartTotal <= 0) {
              return getCachedRate(baseCurrency, activeCurrency);
          }

          const rate = activeCartTotal / baseCartTotal;

          if (Number.isFinite(rate) && rate > 0) {
              setCachedRate(baseCurrency, activeCurrency, rate);
              return rate;
          }
      } catch (error) {
      }

      return getCachedRate(baseCurrency, activeCurrency);
  };

  const resolvePresentmentAmount = async function (baseAmount, options = {}) {
      const normalizedBaseAmount = Number(baseAmount);
      const baseCurrency = options.baseCurrency || getBaseCurrency();
      const activeCurrency = options.activeCurrency || getActiveCurrency();

      if (!Number.isFinite(normalizedBaseAmount) || normalizedBaseAmount <= 0) {
          return normalizedBaseAmount;
      }

      if (!baseCurrency || !activeCurrency || baseCurrency === activeCurrency) {
          return normalizedBaseAmount;
      }

      let rate = getDirectCurrencyRate(baseCurrency, activeCurrency);

      if ((!Number.isFinite(rate) || rate <= 0) && window.Currency?.convert) {
          const convertedAmount = Number(window.Currency.convert(normalizedBaseAmount, baseCurrency, activeCurrency));

          if (Number.isFinite(convertedAmount) && convertedAmount > 0) {
              rate = convertedAmount / normalizedBaseAmount;
              setCachedRate(baseCurrency, activeCurrency, rate);
          }
      }

      if (!Number.isFinite(rate) || rate <= 0) {
          rate = await inferCurrencyRateFromCart({
              activeCartTotal: Number(options.activeCartTotal),
              activeCurrency,
              baseCurrency
          });
      }

      if (!Number.isFinite(rate) || rate <= 0) {
          if (activeCurrency === 'BAM') {
              return normalizedBaseAmount * 2;
          }

          return normalizedBaseAmount;
      }

      return Number((normalizedBaseAmount * rate).toFixed(2));
  };

  // Expose formatMoney globally
  window.formatMoney = formatMoney;
  window.currencyUtils = {
      getActiveCurrency,
      getBaseCurrency,
      getDirectCurrencyRate,
      inferCurrencyRateFromCart,
      resolvePresentmentAmount
  };
})();
